import { ButtonCloseModalPopup } from '@/components/ui/ButtonCloseModalPopup'
import { setPopupOpened, type PopupInfo } from '@/stores/temp'
import { Popup, useLibmapState } from '@lc/libmap-react'
import type { AnchorPosition } from '@lc/libmap-types'
import { classes } from '@lc/libreact'
import { safeProp } from '@lc/libutils'
import { useRef } from 'react'
import { useDispatch } from 'react-redux'
import styles from './MapPopup.scss'
import { popupContent } from './popupContent'

const anchorClass: { [name: string]: string } = {
	'top-left': 'topLeft',
	'bottom-left': 'bottomLeft',
	'left': 'left',
	'top-right': 'topRight',
	'bottom-right': 'bottomRight',
	'right': 'right',
	'bottom': 'bottom',
	'top': 'top',
}

const anchorOffset: {
	[name: string]: [number, number]
} = {
	'left': [12, -3],
	'right': [-12, -3],
	'bottom': [0, -19],
	'top': [0, 13],
	'top-left': [13, -19],
	'top-right': [-13, -19],
	'bottom-right': [-15, 15],
	'bottom-left': [15, 15],
}

export function MapPopup({
	anchor = 'top-left',
	offset,
	tip = true,
	permanent = false,
	type,
	pos,
	id,
}: {
	anchor?: AnchorPosition
	offset?: [number, number]
	tip?: boolean
	permanent?: boolean
} & PopupInfo) {
	const mapstate = useLibmapState()
	const moved = useRef(false)
	const popupRef = useRef<HTMLDivElement>(null)
	const dispatch = useDispatch()

	const popupContentForType = popupContent[safeProp(type)]
	if (!popupContentForType) throw new Error(`No such popup type ${type}`)

	return (
		<Popup
			pos={pos}
			anchor={anchor}
			offset={offset ?? anchorOffset[safeProp(anchor)]}
			permanent
			ref={div => {
				// recenter mechanism only applies to permanent popup
				if (!permanent) return

				if (!div) return
				if (!mapstate?.lib.flyTo) return
				if (!mapstate.map) return
				if (moved.current) return
				moved.current = true
				const rect = div.getBoundingClientRect()
				const padding = mapstate.map.getPadding()

				const isCutOff =
					rect.left < padding.left ||
					rect.top < padding.top ||
					rect.right > window.innerWidth - padding.right ||
					rect.bottom > window.innerHeight - padding.bottom

				if (isCutOff) {
					mapstate.lib.flyTo(mapstate.map, {
						center: pos,
					})
				}
			}}
		>
			<div
				ref={popupRef}
				className={classes(
					styles.container,
					styles[anchorClass[safeProp(anchor)]],
				)}
			>
				{tip && (
					<div
						className={classes(
							styles.tip,
							styles[anchorClass[safeProp(anchor)]],
						)}
					></div>
				)}
				<div className={classes(styles.popup)}>
					{permanent && (
						<ButtonCloseModalPopup
							onClose={() => dispatch(setPopupOpened(null))}
						/>
					)}
					<div
						className={classes(styles.popupTitle, [
							styles.tooltip,
							permanent && !tip,
						])}
					>
						<popupContentForType.Title
							id={id}
							type={type}
							pos={pos}
						/>
					</div>
					<div className={styles.popupContent}>
						<popupContentForType.Content
							id={id}
							type={type}
							pos={pos}
						/>
					</div>
					{!!popupContentForType.buttons.length && (
						<div className={styles.footerButtons}>
							{popupContentForType.buttons.map(
								(PopupButton, i) => (
									<PopupButton
										key={`${id}-${safeProp(i)}`}
										id={id}
										type={type}
										pos={pos}
									/>
								),
							)}
						</div>
					)}
				</div>
			</div>
		</Popup>
	)
}
